import { isEqual, uniqueId } from "lodash";
import * as React from "react";

import { ProviderDropdownTypes, ExportType } from "@app/exports/enumerations";
import { GeographyService } from "@app/geography";
import { ProjectService } from "@app/projects";
import { AccessSummary } from "@app/projects/results/adequacy";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { RegionGranularity } from "@app/region-sets";
import { AccordionContainer, Accordion, formatPhoneNumber, formatUnconvertedDateCustom, SearchBox, TooltipWrapper } from "@app/shared";
import { RoleRestricted, Roles, UserContextProps, withUserContext } from "@app/user";
import { Link } from "react-router-dom";
import { CalculationMethod, EntityTypeEnum, LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, ProviderNetworkType } from "../enumerators";
import { CountyProjectContextProps, withCountyProjectContext } from "../CountyProjectContext";
import ServicingProvider from "../results/servicing-provider/servicingProvider";
import { dismiss, toast } from "./DistanceResultToast";
import ServicingProviderProfile from "./ServicingProviderProfile";
import { TerminatePanelMode, WrappedTerminateProvidersPanel as TerminateProvidersPanel } from "./TerminateProvidersPanel";
import { GainsPercentEnum, NPF } from "./types";

import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import {
  restrictByFields,
  TableType,
  ViewableFieldsContextProps,
  withViewableFields,
} from "@app/viewable-fields";
import NextClosestProvider from "../results/servicing-provider/nextClosestProvider";
import NextClosestProviderResult from "../results/servicing-provider/nextClosestProviderResult";
import { buildAdequacyProvidersColumnConfig, getCargoDataForProvider, getEntityIconClasses } from "./ServicingProviderHelpers";
import NextClosestProviderService from "./nextClosestProviderService";
import { ServicingProviderResult } from "../results/servicing-provider";
import { FlyoutContextOutgoingProps, FlyoutLink, withFlyoutContext } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { capitalizeFirstLetter } from "@app/shared/utility";
import { QESButton } from "@app/shared/qes-standards";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum, ProviderClaimsFilters, NewPatientClaimsEnum } from "@app/projects/servicing-provider";
import { ResourceType } from "@app/resources";
import { DisplayOptions } from "../network-assessment/DisplayOptions";
import { ZipContextState } from "../zip-projects/interfaces";
import { withZipProjectContext } from "../zip-projects/ZipProjectContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { Features } from "@app/feature-flags";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { JSX } from "react";
import { WrappedTerminateProvidersSimulationPanel } from "./provider-termination/TerminateProvidersSimulationPanel";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";

type NetworkProvidersPanelProps = CountyProjectContextProps & ZipContextState & ViewableFieldsContextProps & FeatureFlagContextProps & UserContextProps & FlyoutContextOutgoingProps & {
  accessSummary: AccessSummary;
  className?: string;
  exportEndpoint: string;
  nextClosestProviders: boolean;
  projectURL: string;
  regionSetId: number;
  showPatternOfCareNetworkProviders?: boolean; // passed through to create report/exportdata
  specialtyGroupCode: string;
};

//type TableModel = ServicingProvider & NetworkServicingProviderTerminationModel & NextClosestProvider;

interface NetworkProvidersPanelState {
  accessStandardText: string;
  columnConfig: ColumnConfig;
  displayOptionsDirty: boolean;
  isFiltered: boolean;
  isOpen: boolean;
  lastSearchValue: string;
  lastSelectedEffectivenessValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedEfficiencyValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
  lastSelectedLocationConfidenceValues: LocationConfidenceScoresLabelEnum[];
  lastSelectedMotiveAppropriatenessValues: MotiveScoreValueEnum[];
  lastSelectedNewPatientClaimsValues: NewPatientClaimsEnum[];
  lastSelectedVolumeLabels: ClaimsActivityVolumeLabelEnum[];
  lastSortedCargo: string;
  lastSortedColumn: keyof NetworkProviderTableDataWithCargo;
  lastSortedDirection: SortDirections;
  loadStatus: LoadStatus;
  offset: number;
  selectedProviders: ServicingProvider[] | NetworkServicingProviderTerminationModel[];
  showAverageTime: boolean;
  showAverageDistance: boolean;
  sortedRecords: ServicingProvider[];
  tableDataProviders: NetworkProviderTableDataWithCargo[];
  tableDataTerminations: NetworkProviderTableDataWithCargo[];
  tableType: TableType;
  tableTypeChanged: boolean;
  termedRecords: NetworkServicingProviderTerminationModel[];
  totalCount: number;
}

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

//enum NCPF {
//  AverageDistance = "AverageDistance",
//  AverageTime = "AverageTime",
//  IsServicing = "IsServicing",
//  MemberCount = "MemberCount",
//}

interface NetworkProviderTableData {
  [wafflesKeyColumnName]: string;
  acceptingNewPatients?: string;
  acceptsMedicareAssignment?: string;
  address: JSX.Element;
  availability?: JSX.Element;
  contractType?: string;
  countyName?: string;
  countyState?: string;
  countyFIPS?: string;
  countySSA?: string;
  credentials?: string;
  letterOfIntent?: string;
  daysSinceLastAttested?: string;
  displayName: JSX.Element;
  effectivenessRank?: string;
  efficiencyRank?: string;
  employmentStatus?: string;
  externLocationId?: string;
  externProviderId?: string;
  externRowId?: string;
  externSpecialtyCode?: string;
  externZip?: string;
  firstName?: string;
  gender?: string;
  geoInfo?: string;
  lastName?: string;
  latitude?: number;
  longitude?: number;
  medGroupAffiliation?: string;
  medicareCCN?: string;
  medicareCertifiedBeds?: number;
  middleName?: string;
  averageDistance?: number;
  averageTime?: number;
  isServicing?: string;
  memberCount?: number;
  nppesCertificationDate?: string;
  nppesLastUpdatedDate?: string;
  prefix?: string;
  servesPercent?: string;
  specialty?: string;
  suffix?: string;
  taxonomyCode?: string;
  terminationReason?: JSX.Element;
  underContract?: string;
  mipsScore?: string;
  commercialClaimsVolume?: string;
  commercialNewPatientClaims?: string;
  medicaidClaimsVolume?: string;
  medicaidNewPatientClaims?: string;
  medicareClaimsVolume?: string;
  medicareNewPatientClaims?: string;
  vaClaimsVolume?: string;
  vaNewPatientClaims?: string;
  tricareClaimsVolume?: string;
  tricareNewPatientClaims?: string;
  totalClaimsVolume?: string;
  totalNewPatientClaims?: string;
  LocationConfidenceScore?: string;
  motiveScore?: string;
  careJourneyScoresCostScore?: string;
  careJourneyScoresQualityScore?: string;
}

type NetworkProviderTableDataWithCargo = {
  [cargoKey: string]: string | number | JSX.Element;
} & NetworkProviderTableData;

class NetworkProvidersPanel extends React.Component<NetworkProvidersPanelProps, NetworkProvidersPanelState> {
  private readonly batchCount: number = 100;
  private readonly geoService = new GeographyService();
  private networkTotalCount = 0;
  private readonly projectService = new ProjectService();
  private readonly nextClosestProviderService = new NextClosestProviderService();
  private readonly providerTerminationService = new ProviderTerminationService();
  private readonly toastId = uniqueId("distance-toast-");
  private isZipProject: boolean;
  private cargoColumns = new Set<string>();
  private providerPlurality = "providers";

  constructor(props: NetworkProvidersPanelProps) {
    super(props);

    this.state = {
      accessStandardText: "",
      columnConfig: {},
      displayOptionsDirty: false,
      isFiltered: false,
      isOpen: false,
      lastSearchValue: "",
      lastSelectedEffectivenessValues: undefined,
      lastSelectedEfficiencyValues: undefined,
      lastSelectedLineOfBusiness: undefined,
      lastSelectedLocationConfidenceValues: undefined,
      lastSelectedMotiveAppropriatenessValues: undefined,
      lastSelectedNewPatientClaimsValues: undefined,
      lastSelectedVolumeLabels: undefined,
      lastSortedCargo: undefined,
      lastSortedColumn: undefined,
      lastSortedDirection: SortDirections.None,
      loadStatus: "shouldLoad",
      offset: 0,
      selectedProviders: [],
      showAverageDistance: false,
      showAverageTime: false,
      sortedRecords: [],
      tableDataProviders: [],
      tableDataTerminations: [],
      tableType: props.nextClosestProviders ? TableType.NextClosestNetworkProviders : TableType.NetworkProviders,
      tableTypeChanged: false,
      termedRecords: [],
      totalCount: 0,
    };

    this.isZipProject = this.props.project.resource.resourceType === ResourceType.ZipAnalysisProject;
  }

  public async componentDidMount() {
    const newColumnConfig = this.getColumnConfig();
    this.setState({
      columnConfig: newColumnConfig,
    });
  }

  public async componentDidUpdate(prevProps: NetworkProvidersPanelProps, prevState: NetworkProvidersPanelState) {
    if (this.props.shouldRefreshNetworkProviders) {
      this.props.setShouldRefreshNetworkProviders(false);
      this.buildServicingProviders(true, undefined, undefined, undefined, true);
    }

    if (prevState.loadStatus === "loaded" && this.state.loadStatus === "shouldLoad") {
      await this.buildServicingProviders(true);
    }

    const isFiltered = !!this.state.lastSearchValue ||
      !!this.state.lastSelectedVolumeLabels ||
      !!this.state.lastSelectedLineOfBusiness ||
      !!this.state.lastSelectedEffectivenessValues ||
      !!this.state.lastSelectedEfficiencyValues ||
      !!this.state.lastSelectedLocationConfidenceValues ||
      !!this.state.lastSelectedMotiveAppropriatenessValues ||
      !!this.state.lastSelectedNewPatientClaimsValues;

    if (this.state.tableType === TableType.ProviderTerminations && !isFiltered && this.state.termedRecords.length === 0) {
      this.setState({
        tableType: TableType.NetworkProviders,
      });
    }

    if (!isEqual(prevProps.viewableFieldsByTableType.get(this.state.tableType), this.props.viewableFieldsByTableType.get(this.state.tableType)) ||
      prevState.showAverageDistance !== this.state.showAverageDistance ||
      prevState.showAverageTime !== this.state.showAverageTime ||
      !isEqual(prevProps.runConfiguration, this.props.runConfiguration) ||
      prevState.tableType !== this.state.tableType) {
      const newColumnConfig = this.getColumnConfig();
      this.setState({
        columnConfig: newColumnConfig,
      });
    }

    if (!!prevState.tableType && !isEqual(prevState.tableType, this.state.tableType)) {
      this.setState({
        tableTypeChanged: true,
      });
    }
  }

  public componentWillUnmount() {
    dismiss(this.toastId);
  }

  public render() {
    const initialLoading = !this.state || (this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad");
    const title = this.getTitle();
    this.providerPlurality = this.state.selectedProviders.length === 1 ? "provider" : "providers";

    return (
      <AccordionContainer className={this.props.className}>
        <Accordion
          className={this.props.className}
          defaultOpen={false}
          title={title}
          onPanelWillOpen={initialLoading ? async () => await this.buildServicingProviders() : null}
          onPanelDidOpen={this.handleOnOpen}
          onPanelDidClose={this.handleOnClose}
          testId={this.props.nextClosestProviders ? "next-closest-network-providers-panel" : "network-providers-panel"}
        >
          {this.renderSearchField()}
          {this.renderProviderTable()}
        </Accordion>
      </AccordionContainer>
    );
  }

  private buildServicingProviders = async (
    forceLoad?: boolean,
    selectedColumn?: keyof NetworkProviderTableDataWithCargo,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    resetOffset?: boolean,
    searchValue?: string,
    selectedEffectivenessValues: EffectivenessEfficiencyValueEnum[] = this.state.lastSelectedEffectivenessValues,
    selectedEfficiencyValues: EffectivenessEfficiencyValueEnum[] = this.state.lastSelectedEfficiencyValues,
    selectedVolumeLabels: ClaimsActivityVolumeLabelEnum[] = this.state.lastSelectedVolumeLabels,
    selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum = this.state.lastSelectedLineOfBusiness,
    selectedLocationConfidenceValues: LocationConfidenceScoresLabelEnum[] = this.state.lastSelectedLocationConfidenceValues,
    selectedMotiveAppropriatenessValues: MotiveScoreValueEnum[] = this.state.lastSelectedMotiveAppropriatenessValues,
    selectedNewPatientClaimsValues: NewPatientClaimsEnum[] = this.state.lastSelectedNewPatientClaimsValues
  ) => {
    try {
      // we fetch these here to make sure the viewable fields are up to date, e.g. if different timeslices
      // have different provider sets
      this.props.getByTableType(this.state.tableType);

      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad" || forceLoad
        ? "loading"
        : (this.state.loadStatus === "loaded" && this.state.offset < this.state.totalCount)
          ? "partialLoading"
          : null;
      let noServicingProviders: boolean;

      // we can't assign this.state.lastSearchValue as the default in the parameter list because an empty string is falsey gets replaced
      const searchString = searchValue === undefined ? this.state.lastSearchValue : searchValue;

      if (nextLoadStatus) {
        this.setState((prevState) => ({
          ...prevState,
          loadStatus: nextLoadStatus,
        }));

        let offset = this.state.offset;
        if ((selectedColumn && selectedColumn !== this.state.lastSortedColumn) || (sortDirection !== undefined && sortDirection !== this.state.lastSortedDirection) || resetOffset) {
          offset = 0;
        }

        if (this.props.project.showPciData &&
          selectedLineOfBusiness === undefined &&
          selectedVolumeLabels === undefined &&
          selectedEffectivenessValues === undefined &&
          selectedEfficiencyValues === undefined &&
          selectedLocationConfidenceValues === undefined &&
          selectedMotiveAppropriatenessValues === undefined &&
          selectedNewPatientClaimsValues === undefined
        ) {
          // pci-specific defaults for on load
          selectedEffectivenessValues = Object.values(EffectivenessEfficiencyValueEnum);
          selectedEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum);
          selectedLineOfBusiness = ClaimsActivityLineOfBusinessTypeEnum.Total;
          selectedVolumeLabels = [...Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => x)];
          selectedLocationConfidenceValues = Object.values(LocationConfidenceScoresLabelEnum);
          selectedMotiveAppropriatenessValues = Object.values(MotiveScoreValueEnum);
          selectedNewPatientClaimsValues = Object.values(NewPatientClaimsEnum);
        }

        const servicingProviderResult: ServicingProviderResult = this.props.nextClosestProviders
          ? await this.nextClosestProviderService.nextClosestNetworkProviders(
            this.props.project.projectId,
            this.props.regionSetId,
            this.props.runConfiguration.projectRunConfigurationId,
            this.props.specialtyGroupCode,
            this.props.runConfiguration.networkAssessmentAnalysisType,
            offset,
            this.batchCount,
            selectedColumn as string, // I don't know why selectedColum: keyof NetworkProviderTableDataWithCargo is being parsed as type string | number
            sortDirection,
            columnSubKey,
            searchString,
            selectedEffectivenessValues,
            selectedEfficiencyValues,
            selectedVolumeLabels,
            selectedLineOfBusiness,
            selectedLocationConfidenceValues,
            selectedMotiveAppropriatenessValues,
            selectedNewPatientClaimsValues,
          )
          : await this.projectService.servicingProviders(
            this.props.project.projectId,
            this.props.regionSetId,
            this.props.runConfiguration.projectRunConfigurationId,
            this.props.specialtyGroupCode,
            ProviderNetworkType.Network,
            this.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : this.props.runConfiguration.networkAssessmentAnalysisType,
            GainsPercentEnum.All,
            offset,
            this.batchCount,
            selectedColumn as string,
            sortDirection,
            columnSubKey,
            // if we're viewing termed, and there's a search, we want the user to be able to just toggle off termed
            // and it'll go back to the full list of servicing, instead of a filtered list with no indication of being searched
            this.state.tableType === TableType.NetworkProviders ? searchString : "",
            this.state.tableType === TableType.NetworkProviders ? selectedEffectivenessValues : undefined,
            this.state.tableType === TableType.NetworkProviders ? selectedEfficiencyValues : undefined,
            this.state.tableType === TableType.NetworkProviders ? selectedVolumeLabels : undefined,
            this.state.tableType === TableType.NetworkProviders ? selectedLineOfBusiness : undefined,
            this.state.tableType === TableType.NetworkProviders ? selectedLocationConfidenceValues : undefined,
            this.state.tableType === TableType.NetworkProviders ? selectedMotiveAppropriatenessValues : undefined,
            this.state.tableType === TableType.NetworkProviders ? selectedNewPatientClaimsValues : undefined
          );

        let loadStatus: LoadStatus = "error";
        let newRecords: ServicingProvider[];

        let termedProviders: NetworkServicingProviderTerminationModel[] = [];
        if ((this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) || this.props.user.roles.includes(Roles.TERM_PROVIDER_VIEWER))
          && !this.props.nextClosestProviders && !this.isZipProject) {
          termedProviders = await this.providerTerminationService.getTerminatedProviders(
            this.props.project.projectId,
            this.props.runConfiguration.projectRunConfigurationId,
            this.props.regionSetId,
            this.props.specialtyGroupCode,
            this.props.networkAssessmentAnalysisType,
            selectedColumn as string,
            sortDirection,
            columnSubKey,
            this.state.tableType === TableType.ProviderTerminations ? searchString : "",
            this.state.tableType === TableType.ProviderTerminations ? selectedEffectivenessValues : undefined,
            this.state.tableType === TableType.ProviderTerminations ? selectedEfficiencyValues : undefined,
            this.state.tableType === TableType.ProviderTerminations ? selectedVolumeLabels : undefined,
            this.state.tableType === TableType.ProviderTerminations ? selectedLineOfBusiness : undefined,
            this.state.tableType === TableType.ProviderTerminations ? selectedLocationConfidenceValues : undefined,
            this.state.tableType === TableType.ProviderTerminations ? selectedMotiveAppropriatenessValues : undefined,
            this.state.tableType === TableType.ProviderTerminations ? selectedNewPatientClaimsValues : undefined
          );

          //Update the servicingProviderResult providers to have isTerminated set to the existence of the provider id in the list of termed providers.
          const termedProviderIdSet = new Set(termedProviders.map((termedProvider) => termedProvider.providerId));
          servicingProviderResult.providers.forEach((provider) => provider.isTerminated = termedProviderIdSet.has(provider.providerId));
        }

        const isFiltered = !!searchString || !!selectedVolumeLabels || !!selectedLineOfBusiness || !!selectedEffectivenessValues || !!selectedEfficiencyValues || !!selectedLocationConfidenceValues || !!selectedMotiveAppropriatenessValues || !!selectedNewPatientClaimsValues;

        if (servicingProviderResult && servicingProviderResult.providers) {
          if (servicingProviderResult.providers.length > 0 || isFiltered || termedProviders.length > 0 || forceLoad) {
            if (forceLoad) {
              newRecords = servicingProviderResult.providers;
            } else {
              newRecords = [...this.state.sortedRecords, ...servicingProviderResult.providers];
            }
            loadStatus = "loaded";
            servicingProviderResult.providers.forEach((provider) => {
              provider.cargoData.forEach((cd) => {
                this.cargoColumns.add(cd.name);
              });
            });
          } else {
            newRecords = this.state.sortedRecords.map((r) => r);
            loadStatus = "empty";
          }
        }

        noServicingProviders = servicingProviderResult.totalCount === 0;
        this.networkTotalCount = servicingProviderResult.totalCount;
        const newTableType =
          this.props.nextClosestProviders ? TableType.NextClosestNetworkProviders :
            isFiltered ? this.state.tableType :
              noServicingProviders && termedProviders.length > 0 ?
                TableType.ProviderTerminations :
                this.state.tableType;

        this.setState((prevState) => ({
          ...prevState,
          ...servicingProviderResult,
          accessStandardText: this.props.nextClosestProviders && (servicingProviderResult as NextClosestProviderResult).accessStandardText,
          lastSearchValue: searchString,
          lastSelectedEffectivenessValues: selectedEffectivenessValues,
          lastSelectedEfficiencyValues: selectedEfficiencyValues,
          lastSelectedLineOfBusiness: selectedLineOfBusiness,
          lastSelectedLocationConfidenceValues: selectedLocationConfidenceValues,
          lastSelectedMotiveAppropriatenessValues: selectedMotiveAppropriatenessValues,
          lastSelectedNewPatientClaimsValues: selectedNewPatientClaimsValues,
          lastSelectedVolumeLabels: selectedVolumeLabels,
          lastSortedCargo: columnSubKey,
          lastSortedColumn: selectedColumn,
          lastSortedDirection: sortDirection,
          loadStatus,
          offset: offset + (servicingProviderResult.batchCount || this.batchCount),
          showAverageDistance: this.props.nextClosestProviders && (servicingProviderResult as NextClosestProviderResult).showDistance,
          showAverageTime: this.props.nextClosestProviders && (servicingProviderResult as NextClosestProviderResult).showTime,
          sortedRecords: newRecords,
          tableDataProviders: this.buildTable(newRecords),
          tableDataTerminations: this.buildTable(termedProviders),
          tableType: newTableType,
          termedRecords: termedProviders,
          totalCount: (newTableType === TableType.NetworkProviders || newTableType === TableType.NextClosestNetworkProviders) ? servicingProviderResult.totalCount : termedProviders.length,
        }), () => {
          this.setState({
            columnConfig: this.getColumnConfig(),
          });
        });
      }
    } catch (ex) {
      this.setState((prevState) => ({
        ...prevState,
        loadStatus: "error",
        sortedRecords: new Array<ServicingProvider>(),
        tableDataProviders: new Array<NetworkProviderTableDataWithCargo>(),
        tableDataTerminations: new Array<NetworkProviderTableDataWithCargo>(),
        termedRecords: new Array<NetworkServicingProviderTerminationModel>(),
      }));
    }
  }

  private getProviderProfile = (provider: ServicingProvider): React.ReactNode => {
    return (
      <ServicingProviderProfile
        {...this.props}
        isTerminated={this.state.tableType === TableType.ProviderTerminations}
        nextClosestProvider={this.props.nextClosestProviders}
        provider={provider}
        providerNetworkType={ProviderNetworkType.Network}
        isZipProject={this.isZipProject}
        isSSAToFIPSFeatureEnabled={this.props.enabledFeatureFlags.has(Features.SSA_TO_FIPS)}
      />
    );
  }

  private handleOnClose = () => {
    this.setState({ isOpen: false });
  }

  private handleOnOpen = () => {
    this.setState({ isOpen: true });
  }

  private handleDistanceClick = async () => {
    if (this.state.selectedProviders.length === 2) {
      const fromProvider = this.state.selectedProviders[0];
      const toProvider = this.state.selectedProviders[1];
      const distance = await this.geoService.getDrivingDistance(fromProvider.longitude, fromProvider.latitude, toProvider.longitude, toProvider.latitude);

      toast(distance, this.toastId);
    }
  }

  private handleProviderSearch = (
    searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
  ): void => {
    this.buildServicingProviders(
      true,
      this.state.lastSortedColumn,
      this.state.lastSortedDirection,
      this.state.lastSortedCargo,
      true,
      searchValue,
      selectedEffectivenessValues,
      selectedEfficiencyValues,
      selectedVolumeLabels,
      selectedLineOfBusiness,
      selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues
    );
  }

  private updateChangeTracker = () => {
    if (this.state.tableTypeChanged) {
      this.setState({
        tableTypeChanged: false,
      });
    }
  }

  private renderSearchField = (): JSX.Element => {
    if (this.props.project.showPciData) {
      return (
        <ProviderClaimsFilters
          clearFilters={this.state.tableTypeChanged}
          handleProviderSearch={this.handleProviderSearch}
          lastSearchValue={this.state.lastSearchValue}
          lastSelectedEffectivenessValues={this.state.lastSelectedEffectivenessValues}
          lastSelectedEfficiencyValues={this.state.lastSelectedEfficiencyValues}
          lastSelectedGainsPercent={null}
          lastSelectedLineOfBusiness={this.state.lastSelectedLineOfBusiness}
          lastSelectedVolumeLabels={this.state.lastSelectedVolumeLabels}
          lastSelectedLocationConfidenceValues={this.state.lastSelectedLocationConfidenceValues}
          lastSelectedMotiveAppropriatenessValues={this.state.lastSelectedMotiveAppropriatenessValues}
          lastSelectedNewPatientClaimsValues={this.state.lastSelectedNewPatientClaimsValues}
          tableType={this.state.tableType}
          validLoadStatus={this.state.loadStatus === "loaded" || this.state.loadStatus === "empty"}
          showLocationConfidence={this.props.project.showLocationConfidenceScores && this.props.runConfiguration.showLocationConfidenceScores}
          updateChangeTracker={this.updateChangeTracker}
          showNewPatientClaims={true}
        />
      );
    } else {
      return (
        <SearchBox
          key={this.state.tableType}
          descriptiveText={`This will search each provider's name, NPI,${this.props.user.roles.includes(Roles.TIN_VIEWER) ? " TIN," : ""} specialty, and address.`}
          fieldsetClass="d-flex"
          inputLabel="Search Providers"
          testId={`${this.state.tableType}-provider-search`}
          onSubmit={(value: string) => { this.handleProviderSearch(value) }}
        />
      );
    }
  }

  private handleLoadMore = (justChecking: boolean) => {
    if (!justChecking) {
      if (this.state.tableType === TableType.ProviderTerminations) {
        this.setState((prevState) => ({
          offset: prevState.offset + this.batchCount,
        }));
        return (this.state.offset + this.batchCount) < this.state.termedRecords.length;
      } else {
        this.buildServicingProviders(false, this.state.lastSortedColumn, this.state.lastSortedDirection, this.state.lastSortedCargo, false);
      }
      // if we're going to be returning more providers, we need to consider the additional batchCount in the total
      return (this.state.offset + this.batchCount) < this.state.totalCount;
    }
    // but if we're "just checking" to see if we should render the button, we don't need to consider the addition
    if (this.state.tableType === TableType.ProviderTerminations) {
      return this.state.offset < this.state.termedRecords.length;
    } else {
      return this.state.offset < this.state.totalCount;
    }
  }

  private renderProviderTable = () => {
    const calcMethodText = this.props.accessSummary
      ? this.getDisplayTextForCalculationMethod(this.props.accessSummary.calculationMethod)
      : null;

    let leadText: JSX.Element = null;

    if (this.props.nextClosestProviders) {
      leadText = (<p>The following are based on members without access. These Providers are outside access standard of {this.state.accessStandardText}.</p>);
    } else if (calcMethodText) {
      leadText = (<p>The following network providers are within the {calcMethodText} standards.</p>);
    }

    const hasTermedProviders = (this.props.runConfiguration.isLatestPublished || this.props.runConfiguration.datePublished == null) && this.state.termedRecords.length > 0;
    const isTermedTimeslice = this.props.runConfiguration.isTermed || this.props.runConfiguration.isProjectedTermed;
    const isShowingTerminations = this.state.tableType === TableType.ProviderTerminations;

    return (
      <>
        <div className="d-flex justify-content-between">
          {leadText}
          {!this.props.nextClosestProviders && !this.isZipProject && isTermedTimeslice &&
            (
              <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN, Roles.TERM_PROVIDER_VIEWER]}>
                {(hasTermedProviders || this.state.tableType === TableType.ProviderTerminations) && (
                  <QESButton
                    qesType="secondary"
                    qesTestId="network-providers-panel__toggle-terminated-providers"
                    className="font-weight-normal p-0 mr-3"
                    onClick={() => { this.setDisplayedProviderType(isShowingTerminations ? TableType.NetworkProviders : TableType.ProviderTerminations); }}
                    disabled={!(this.state.loadStatus === "loaded" || this.state.loadStatus === "empty")}
                  >
                    <QesIcon
                      iconName={isShowingTerminations ? QesIcons.FontAwesome.ToggleOn : QesIcons.FontAwesome.ToggleOff}
                      description={`toggle the display of terminated providers ${isShowingTerminations ? "off" : "on"}`}
                      width="16"
                      classNames="align-baseline"
                    /> Terminated
                  </QESButton>
                )}
              </RoleRestricted>
            )
          }
        </div>
        {this.renderTable()}
      </>
    );
  }

  private renderTable = () => {
    return (
      <Waffles<NetworkProviderTableDataWithCargo>
        bulkActionBar={this.bulkActionHeader()}
        columns={this.state.columnConfig}
        customSort={this.handleSort}
        data={this.state.tableType === TableType.ProviderTerminations ? this.state.tableDataTerminations.slice(0, this.state.offset) : this.state.tableDataProviders}
        fullWidth={true}
        key={this.state.tableType} // change the key on table type change so it resets the internal sort direction
        loading={this.state.loadStatus !== "loaded" && this.state.loadStatus !== "empty"}
        loadMore={this.handleLoadMore}
        handleCheckboxChange={this.onCheckboxChange}
        passThroughSorting={true}
        selectedKeys={this.state.selectedProviders.map((provider) => provider.providerId.toString())}
        showCheckboxes={true}
        testId="adequacy-network-providers"
        totalNumberOfRecords={this.state.totalCount}
      />
    );
  }

  // ServicingProvider[] works for the param because NetworkServicingProviderTerminationModel extends ServicingProvider, and all its extra properties just go in the terminationReason field
  private buildTable = (inputRecords: ServicingProvider[] = this.state.tableType === TableType.ProviderTerminations ? this.state.termedRecords : this.state.sortedRecords) => {
    const viewableFields = this.props.viewableFieldsByTableType.get(this.state.tableType);
    if (!viewableFields?.length) {
      return [];
    }

    const tableData: NetworkProviderTableDataWithCargo[] = inputRecords.map((provider) => {
      const providerCargoData: Partial<NetworkProviderTableDataWithCargo> = getCargoDataForProvider(provider, viewableFields, this.cargoColumns);
      const checkSquareOutlineIcon = <QesIcon isDecorative iconName={QesIcons.FontAwesome.CheckSquareOutline} width="13" classNames="align-baseline" />;
      const checkIcon = <QesIcon isDecorative iconName={QesIcons.FontAwesome.Check} width="13" classNames="align-baseline" />;

      /* eslint-disable sort-keys */

      return ({
        [wafflesKeyColumnName]: `${provider.providerId}`,
        displayName: restrictByFields(viewableFields, [NPF.NPI, NPF.DisplayName, NPF.TIN, NPF.OrganizationName, NPF.EntityType], (has) => (
          <>
            {has(NPF.DisplayName) && (
              <FlyoutLink
                classNames="p-0 qes-button-secondary"
                content={{
                  body: this.getProviderProfile(provider) as JSX.Element,
                  title: "Provider Profile",
                }}
                testId={`network-providers-panel__provider-profile-flyout--${provider.providerId}`}
              >
                <i className={getEntityIconClasses(provider.entityType, " supress-panel-styles")} style={{ paddingRight: "16px" }} />
                {provider.displayName}
                {provider.isTerminated && (
                  <TooltipWrapper
                    tooltipWidth="24rem"
                    text={
                      "This record is in your network and contributes to your adequacy results in the latest timeslice with a date. " +
                      "The Termination timeslice shows adequacy results without this record. If you no longer want to do a termination " +
                      "analysis on this record, go to the Termination timeslice and remove the Termination status from this record."
                    }
                    style={{ paddingLeft: "16px" }}
                  >
                    <QesIcon
                      iconName={QesIcons.FontAwesome.UserTimes}
                      description="This provider record has been added to the current termination analysis."
                      width="16"
                      classNames="supress-panel-styles align-baseline" />
                  </TooltipWrapper>
                )}
              </FlyoutLink>
            )}
            {has(NPF.NPI) && !!provider.npi && (
              <span className="text-muted ml15 d-block">NPI: {provider.npi}</span>
            )}
            <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
              {has(NPF.TIN) && !!provider.tin && !!provider.tin && viewableFields.find((field) => field.backingFieldName === NPF.TIN).visible && (
                <span className="text-muted ml15 d-block">TIN: {provider.tin}</span>
              )}
            </RoleRestricted>
            {has(NPF.OrganizationName)
              && !!provider.organizationName
              && provider.entityType === EntityTypeEnum.Individual && (
                <span className="text-muted ml15 d-block">{provider.organizationName}</span>
              )}
          </>
        )),

        averageDistance: (provider as NextClosestProvider).averageDistance,
        averageTime: (provider as NextClosestProvider).averageTime,
        isServicing: (provider as NextClosestProvider).isServicing,
        memberCount: (provider as NextClosestProvider).memberCount,

        specialty: provider.specialty,

        address: restrictByFields(viewableFields, [NPF.Address, NPF.Address2, NPF.City, NPF.State, NPF.Zip, NPF.Phone, NPF.Fax], (has) => (
          <>
            {has(NPF.Address) && provider.address ? <>{provider.address}<br /></> : null}
            {has(NPF.Address2) && provider.address2 ? <>{provider.address2}<br /></> : null}
            {has(NPF.City) && provider.city}
            {has(NPF.City) && (has(NPF.State) || has(NPF.Zip)) ? ", " : null}
            {has(NPF.State) && provider.state} {has(NPF.Zip) && provider.zip}
            {has(NPF.Phone) && provider.phone ? <br /> : null}
            {has(NPF.Phone) && provider.phone ?
              <>
                <TooltipWrapper text="Phone" position="bottom">
                  <QesIcon iconName={QesIcons.FontAwesome.Phone} description="phone number" width="11" classNames="align-baseline d-inline-block mr-1" />
                </TooltipWrapper>
                &nbsp; {formatPhoneNumber(provider.phone)}
              </> :
              null
            }
            {has(NPF.Fax) && provider.fax ? <br /> : null}
            {has(NPF.Fax) && provider.fax ?
              <>
                <TooltipWrapper text="Fax" position="bottom">
                  <QesIcon iconName={QesIcons.FontAwesome.Fax} description="fax number" width="14" classNames="align-baseline d-inline-block mr-1" />
                </TooltipWrapper>
                &nbsp; {formatPhoneNumber(provider.fax)}
              </> :
              null
            }
          </>
        )),

        prefix: provider.prefix,
        firstName: provider.firstName,
        middleName: provider.middleName,
        lastName: provider.lastName,
        suffix: provider.suffix,
        credentials: provider.credentials,
        gender: provider.gender,
        servesPercent: provider.servesPercent !== undefined ? `${provider.servesPercent.toFixed(1)}%` : "",
        availability: restrictByFields(viewableFields, [NPF.DeactivatedNPI, NPF.MedicareOptOut, NPF.OIGExcluded, NPF.OIGWaiver, NPF.Precluded, NPF.InvalidNPI, NPF.MissingNPI], (has) => (
          <>
            {has(NPF.DeactivatedNPI) && provider.deactivatedNPI && <><span>{checkSquareOutlineIcon} Deactivated</span><br /></>}
            {has(NPF.MedicareOptOut) && provider.medicareOptOut && <><RoleRestricted allowedRoles={[Roles.MEDICARE_OPT_OUTS]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkSquareOutlineIcon} Medicare Opt-Out</span></RoleRestricted><br /></>}
            {has(NPF.OIGExcluded) && provider.oigExcluded && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkSquareOutlineIcon} OIG Excluded</span></RoleRestricted><br /></>}
            {has(NPF.OIGWaiver) && provider.oigWaiver && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkSquareOutlineIcon} OIG Waiver</span></RoleRestricted><br /></>}
            {has(NPF.Precluded) && provider.precluded && <><RoleRestricted allowedRoles={[Roles.PRECLUDED_PROVIDER_USER]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkSquareOutlineIcon} Medicare Precluded</span></RoleRestricted><br /></>}
            {has(NPF.InvalidNPI) && provider.invalidNPI && <><span>{checkSquareOutlineIcon} Invalid NPI</span><br /></>}
            {has(NPF.MissingNPI) && provider.missingNPI && <><span>{checkSquareOutlineIcon} Missing NPI</span><br /></>}
          </>
        )),
        taxonomyCode: provider.taxonomyCode,
        acceptingNewPatients: provider.acceptingNewPatients === undefined ? "---" : provider.acceptingNewPatients ? "Y" : "N",
        employmentStatus: provider.employmentStatus,
        contractType: provider.contractType,
        underContract: provider.underContract,
        medGroupAffiliation: provider.medGroupAffiliation,
        medicareCCN: provider.medicareCCN,
        medicareCertifiedBeds: provider.medicareCertifiedBeds,
        acceptsMedicareAssignment: provider.acceptsMedicareAssignment && "Yes",
        externZip: provider.externZip,
        externSpecialtyCode: provider.externSpecialtyCode,
        externRowId: provider.externRowId,
        externProviderId: provider.externProviderId,
        externLocationId: provider.externLocationId,
        letterOfIntent: provider.letterOfIntent !== undefined ? provider.letterOfIntent ? "Y" : "N" : "---",
        daysSinceLastAttested: provider.daysSinceLastAttested && provider.daysSinceLastAttested <= this.props.runConfiguration.recentAttestationDays ?
          `${provider.daysSinceLastAttested} days` :
          "Not Attested",
        isRecentlyAttested: provider.isRecentlyAttested ? "Y" : "N",
        lastAttestedDate: provider.lastAttestedDate ? formatUnconvertedDateCustom(provider.lastAttestedDate, "YYYY-MM-DD") : "---",
        nppesCertificationDate: provider.nppesCertificationDate ? formatUnconvertedDateCustom(provider.nppesCertificationDate, "YYYY-MM-DD") : "",
        nppesLastUpdatedDate: provider.nppesLastUpdatedDate ? formatUnconvertedDateCustom(provider.nppesLastUpdatedDate, "YYYY-MM-DD") : "",
        latitude: provider.latitude,
        longitude: provider.longitude,
        geoInfoDisplay: provider.geoInfoDisplay,
        countyFIPS: provider.countyFIPS ? provider.countyFIPS : "---",
        countySSA: provider.countySSA,
        countyName: provider.countyName,
        countyState: provider.countyState,
        mipsScore: provider.mipsScore ? provider.mipsScore.toString() : "---",
        effectivenessRank: provider.effectivenessRank ? provider.effectivenessRank.toString() : "---",
        efficiencyRank: provider.efficiencyRank ? provider.efficiencyRank.toString() : "---",
        commercialClaimsVolumeDisplay: provider.commercialClaimsVolumeDisplay ? provider.commercialClaimsVolumeDisplay : "---",
        commercialNewPatientClaims: provider.commercialNewPatientClaims ? provider.commercialNewPatientClaims : "---",
        medicaidClaimsVolumeDisplay: provider.medicaidClaimsVolumeDisplay ? provider.medicaidClaimsVolumeDisplay : "---",
        medicaidNewPatientClaims: provider.medicaidNewPatientClaims ? provider.medicaidNewPatientClaims : "---",
        medicareClaimsVolumeDisplay: provider.medicareClaimsVolumeDisplay ? provider.medicareClaimsVolumeDisplay : "---",
        medicareNewPatientClaims: provider.medicareNewPatientClaims ? provider.medicareNewPatientClaims : "---",
        vaClaimsVolumeDisplay: provider.vaClaimsVolumeDisplay ? provider.vaClaimsVolumeDisplay : "---",
        vaNewPatientClaims: provider.vaNewPatientClaims ? provider.vaNewPatientClaims : "---",
        tricareClaimsVolumeDisplay: provider.tricareClaimsVolumeDisplay ? provider.tricareClaimsVolumeDisplay : "---",
        tricareNewPatientClaims: provider.tricareNewPatientClaims ? provider.tricareNewPatientClaims : "---",
        totalClaimsVolumeDisplay: provider.totalClaimsVolumeDisplay ? provider.totalClaimsVolumeDisplay : "---",
        totalNewPatientClaims: provider.totalNewPatientClaims ? provider.totalNewPatientClaims : "---",
        locationConfidenceScoresDisplay: provider.locationConfidenceScoresDisplay ? provider.locationConfidenceScoresDisplay : "---",
        motiveScore: provider.motiveScore ? provider.motiveScore : "---",
        careJourneyScoresCostScore: provider.careJourneyScoresCostScore ? provider.careJourneyScoresCostScore : "---",
        careJourneyScoresQualityScore: provider.careJourneyScoresQualityScore ? provider.careJourneyScoresQualityScore : "---",
        terminationReason: (() => {
          const providerTermination = provider as NetworkServicingProviderTerminationModel;

          let terminationString = "";
          if (!providerTermination.npi && !providerTermination.tin) {
              terminationString += "Name";
          }
          else if (providerTermination.terminateByNPI) {
            terminationString += "NPI";
            if (providerTermination.terminateByTIN) {
              terminationString += " and TIN";
            }
          } else if (providerTermination.terminateByTIN) {
            terminationString += "TIN";
          }
          return (
            <>
              <div className="mb-3">
                {!providerTermination.processed && <div className="font-weight-bold">UNPROCESSED</div>}
                {(this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && this.props.runConfiguration.isLatestPublished) && (
                  <TooltipWrapper text="Edit/View Terminated Provider Details">
                    <FlyoutLink
                      classNames="text-info font-weight-normal p-0"
                      content={{
                        body: this.updateOrViewTerminatedProviderPanel(providerTermination, true),
                        title: "Termination Details",
                      }}
                      testId={`network-providers-panel__termination-details-flyout--edit--${provider.providerId}`}
                    >
                      {providerTermination.reasonName}
                    </FlyoutLink>
                  </TooltipWrapper>
                )}
                {((!this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && this.props.user.roles.includes(Roles.TERM_PROVIDER_VIEWER)) || (this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && this.props.runConfiguration.datePublished == null)) && (
                  <TooltipWrapper text="View Terminated Provider Details">
                    <FlyoutLink
                      classNames="text-info font-weight-normal p-0"
                      content={{
                        body: this.updateOrViewTerminatedProviderPanel(providerTermination, false),
                        title: "Termination Details",
                      }}
                      testId={`network-providers-panel__termination-details-flyout--no-edit--${provider.providerId}`}
                    >
                      {providerTermination.reasonName}
                    </FlyoutLink>
                  </TooltipWrapper>
                )}
              </div>
              <div>
                Terminated by {terminationString}
                {providerTermination.allLocations && <div>{checkIcon} All Locations</div>}
                {providerTermination.allSpecialties && <div>{checkIcon} All Specialties</div>}
              </div>
            </>
          );
        })(),
        ...providerCargoData,
      });
    });
    /* eslint-enable sort-keys */

    return tableData;
  }

  private onCheckboxChange = (newSelectedKeys: string[], isBulk?: boolean) => {
    const currentList = (this.state.tableType === TableType.NetworkProviders || this.state.tableType === TableType.NextClosestNetworkProviders) ? this.state.sortedRecords : this.state.termedRecords;
    let newSelectedProviders = [...this.state.selectedProviders];

    if (isBulk) {
      if (newSelectedKeys.length > 0) {
        newSelectedProviders.push(...currentList.filter((provider) => newSelectedKeys.includes(provider.providerId.toString())));
      } else {
        const removedIds = currentList.map((provider) => provider.providerId);
        newSelectedProviders = newSelectedProviders.filter((provider) => { return !removedIds.includes(provider.providerId); });
      }
    } else {
      if (newSelectedKeys.length > this.state.selectedProviders.length) {
        // one was added
        const addedKey = newSelectedKeys.filter((key) => !this.state.selectedProviders.find((provider) => provider.providerId.toString() === key))[0];
        // since the user clicked its box, it's safe to assume that provider is currently in memory
        const addedProvider = currentList.find((record) => record.providerId.toString() === addedKey);
        newSelectedProviders.push(addedProvider);
      } else if (newSelectedKeys.length < this.state.selectedProviders.length) {
        // one was removed
        const removedIndex = newSelectedProviders.findIndex((provider) => !newSelectedKeys.includes(provider.providerId.toString()));
        newSelectedProviders.splice(removedIndex, 1);
      }
    }
    // now we need to dedupe the array of objects...
    newSelectedProviders = newSelectedProviders.reduce((result, current) => {
      if (!result.find((r) => r.providerId === current.providerId)) {
        result.push(current);
      }
      return result;
    }, new Array<ServicingProvider | NetworkServicingProviderTerminationModel>());

    this.setState({
      selectedProviders: newSelectedProviders,
    }, () => dismiss(this.toastId));
  }

  private handleSort = (records: NetworkProviderTableDataWithCargo[], column: keyof NetworkProviderTableDataWithCargo, direction: SortDirections) => {
    this.buildServicingProviders(true, this.state.columnConfig[column].cargoTitle ? "CargoData" : column, direction, this.state.columnConfig[column].cargoTitle);
    return records;
  }

  private setDisplayedProviderType = (providerType: TableType) => {
    const newTableType: TableType = this.props.nextClosestProviders ?
      TableType.NextClosestNetworkProviders :
      providerType === TableType.NetworkProviders ? TableType.NetworkProviders : TableType.ProviderTerminations;
    this.setState({
      lastSearchValue: "",
      lastSelectedEffectivenessValues: undefined,
      lastSelectedEfficiencyValues: undefined,
      lastSelectedLineOfBusiness: undefined,
      lastSelectedLocationConfidenceValues: undefined,
      lastSelectedMotiveAppropriatenessValues: undefined,
      lastSelectedNewPatientClaimsValues: undefined,
      lastSelectedVolumeLabels: undefined,
      offset: 0,
      tableType: newTableType,
      totalCount: providerType === TableType.NetworkProviders ? (this.networkTotalCount || this.state.sortedRecords.length) : this.state.termedRecords.length,
    }, () => {
      this.buildServicingProviders(true);
    });
  }

  private getDisplayTextForCalculationMethod(calculationMethod: CalculationMethod): string {
    switch (calculationMethod) {
      case CalculationMethod.Time:
        return "time";
      case CalculationMethod.TimeAndDistance:
      case CalculationMethod.TimeOrDistance:
        return "time/distance";
      default:
        return "distance";
    }
  }

  private createTerminateProvidersPanel = () => {
    if (this.props.enabledFeatureFlags.has(Features.SIMULATE_PROVIDER_TERMINATION)) {
      return (
        <WrappedTerminateProvidersSimulationPanel
          onSubmit={() => {
            this.props.setProjectHasUnprocessedTerminations(true);
            this.setState({
              loadStatus: "shouldLoad",
              offset: 0,
            });
          }}
          projectId={this.props.project.projectId}
          providerNetworkType={ProviderNetworkType.Network}
          selectedProviders={this.state.selectedProviders}
          specialtyGroupCode={this.props.specialtyGroupCode}
          terminatePanelMode={TerminatePanelMode.Create}
          regionSetId={this.props.regionSetId}
        />
      );
    }

    return (
      <TerminateProvidersPanel
        onSubmit={() => {
          this.props.setProjectHasUnprocessedTerminations(true);
          this.setState({
            loadStatus: "shouldLoad",
            offset: 0,
          });
        }}
        projectId={this.props.project.projectId}
        selectedProviders={this.state.selectedProviders}
        specialtyGroupCode={this.props.specialtyGroupCode}
        terminatePanelMode={TerminatePanelMode.Create}
      />
    );
  }

  private updateOrViewTerminatedProviderPanel = (terminatedProvider: NetworkServicingProviderTerminationModel, canEdit: boolean) => {
    return (
      <TerminateProvidersPanel
        onSubmit={() => {
          this.setState({
            loadStatus: "shouldLoad",
            offset: 0,
          });
        }}
        projectId={this.props.project.projectId}
        selectedProviders={this.state.selectedProviders}
        specialtyGroupCode={this.props.specialtyGroupCode}
        terminatePanelMode={canEdit ? TerminatePanelMode.Edit : TerminatePanelMode.View}
        termination={terminatedProvider}
      />
    );
  }

  private bulkActionHeader = () => {
    const selectedMemberships: number[] = [this.props.runConfiguration.memberSetId];
    if (this.props.isDualMembershipMode) {
      selectedMemberships.push(this.props.compareToRunConfiguration.memberSetId);
    }

    const terminatedProvidersSelected = this.state.selectedProviders.some((provider) => provider.isTerminated);
    const terminationDisabledTooltipText = this.terminationDisabledToolTip(this.state.selectedProviders);

    return (
      <>
        <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              activeModule: this.props.module,
              activeNetworkType: (this.props.nextClosestProviders ? ProviderDropdownTypes.NextClosestProviders : ProviderNetworkType.Network) as ProviderNetworkType,
              adequacyFilters: this.props.adequacyFilters,
              availableExportTypes: this.isZipProject ? [ExportType.ZipAnalysisAdequacyServicingProvider] : [ExportType.ServicingProviders],
              disableRegionFilters: true,
              disableSpecialtyGroupFilters: true,
              exportNetworkProviderCount: this.state.selectedProviders.length > 0
                ? this.state.selectedProviders.length
                : this.state.tableType === TableType.NetworkProviders ? this.state.totalCount : this.state.termedRecords.length,
              filters: this.props.filters,
              granularity: RegionGranularity.County,
              initialNetworkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              networkTypes: this.props.nextClosestProviders ? [] : [ProviderNetworkType.Network],
              nextClosestProviders: this.props.nextClosestProviders,
              perspective: this.props.perspective,
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.projectURL,
              providerIds: this.state.selectedProviders.map((provider) => provider.providerId),
              regionSetIds: [this.props.regionSetId],
              showAppendNextClosestProviders: this.props.runConfiguration && this.props.runConfiguration.showNextClosestNetworkProviders || false,
              showAppendPatternOfCareProviders: this.props.showPatternOfCareNetworkProviders,
              specialtyGroupCodes: [this.props.specialtyGroupCode],

              // new reportcustomization
              /* eslint-disable sort-keys */
              projectName: this.props.project.resource.name,
              defaultReportType: ReportType.AdequacyServicingProvider,
              defaultProviderSource: this.props.nextClosestProviders ? ProviderDropdownTypes.NextClosestProviders : ProviderNetworkType.Network,
              networkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              primaryMembership: { alias: this.props.project.memberSetAlias || this.props.project.memberSetName, id: this.props.project.memberSetId },
              secondaryMembership: { alias: this.props.project.memberSetAlias2 || this.props.project.memberSetName2, id: this.props.project.memberSetId2 },
              selectedMemberships,
              selectedTimeslice: this.props.runConfiguration.projectRunConfigurationId, // runConfigurationId
              isCompareMode: !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode,
              selectedComparedTimeslice: this.props.compareToRunConfiguration?.projectRunConfigurationId, // compareToRunConfigurationId
              isViewingOpportunity: this.props.adequacyFilters.opportunity,
              showAddedProviders: !!this.props.compareToRunConfiguration,
              showMatchedProviders: !!this.props.compareToRunConfiguration,
              showRemovedProviders: !!this.props.compareToRunConfiguration,
              showPatternOfCare: this.props.project.showPatternOfCareProviders,
              hasProspectProviders: !!this.props.runConfiguration.prospectProviderSetId,
              hasMarketProviders: !!this.props.runConfiguration.marketProviderSetId,
              hasNextClosestProviders: !!this.props.runConfiguration.showNextClosestNetworkProviders,
              showAdvancedComparison: this.props.project.showAdvancedComparison,
              showZipSummariesReport: this.props.project.hasZipSummaries,
              showExpandableSpecialties: !!this.props.adequacyResult?.details?.some((d) => d.canExpandSpecialties),
              /* eslint-enable */
            },
          }}
          data-testid={this.state.tableType + "__create-report-button"}
        >
          <QesIcon iconName={QesIcons.FontAwesome.CloudDownload} isDecorative classNames="align-baseline" /> Create Report
        </Link>

        <TooltipWrapper text="Measure distance between two selected providers" containerClassName="ml-3">
          <QESButton qesType="secondary" className="px-0" disabled={this.state.selectedProviders.length !== 2} onClick={this.handleDistanceClick} qesTestId={`network-providers-panel__distance-button`}>
            <QesIcon isDecorative iconName={QesIcons.FontAwesome.LocationArrow} width="12" classNames="mr-1" /> Distance
          </QESButton>
        </TooltipWrapper>

        {this.props.runConfiguration.isLatestPublished
          && !this.props.project.isEditRestricted
          && !this.props.nextClosestProviders
          && (
            <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
              {this.state.tableType === TableType.NetworkProviders ?
                (
                  <TooltipWrapper
                    tooltipWidth={terminatedProvidersSelected ? "32rem" : "auto"}
                    text={terminatedProvidersSelected ? "We can't process this termination analysis because one or more records selected are already terminated (indicated by the xx icon). " +
                      "You will need to deselect all previously terminated records to run a new termination analysis. " +
                      "Any previously terminated records will still be terminated unless you go to the Terminated timeslice " +
                      "and remove the Termination status from this record."
                      : terminationDisabledTooltipText !== ""
                        ? terminationDisabledTooltipText  
                        : `Terminate selected ${this.providerPlurality}`}
                    containerClassName="ml-3"
                  >
                    {!this.isZipProject && (<FlyoutLink
                      classNames="p-0"
                      content={{
                        body: this.createTerminateProvidersPanel(),
                        title: `Terminate ${capitalizeFirstLetter(this.providerPlurality)}`,
                      }}
                      disabled={!this.state.selectedProviders.length || terminatedProvidersSelected || terminationDisabledTooltipText !== ""}
                      testId="network-providers-panel__terminate-flyout"
                    >
                      <QesIcon iconName={QesIcons.FontAwesome.UserTimes} isDecorative width="16" classNames="align-baseline" /> Terminate
                    </FlyoutLink>)}
                    {this.isZipProject && this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION) && (<FlyoutLink
                      classNames="p-0"
                      content={{
                        body: <>Terminate Zip Providers Flyout</>,
                        title: `Terminate ${capitalizeFirstLetter(this.providerPlurality)}`,
                      }}
                      disabled={!this.state.selectedProviders.length || terminatedProvidersSelected}
                      testId="network-providers-panel__terminate-flyout"
                    >
                      <QesIcon iconName={QesIcons.FontAwesome.UserTimes} isDecorative width="16" classNames="align-baseline" /> Terminate
                    </FlyoutLink>)}
                  </TooltipWrapper>
                ) :
                (
                  <TooltipWrapper text={`Restore selected ${this.providerPlurality}`} containerClassName="ml-3">
                    <ModalLink
                      content={{
                        body: `Are you sure you want to restore the selected ${this.providerPlurality}?`,
                        cancelButtonText: "Cancel",
                        confirmButtonAction: async () => {
                          await this.providerTerminationService.deleteTermination(
                            this.props.project.projectId,
                            this.state.selectedProviders.map((sp) => (sp as NetworkServicingProviderTerminationModel).providerTerminationProjectId)
                          );
                          const unprocessedProviderTerminations = await this.providerTerminationService.getUnprocessedProviderTerminations(this.props.project.projectId);

                          this.props.setProjectHasUnprocessedTerminations(unprocessedProviderTerminations?.length > 0);

                          this.setState({
                            loadStatus: "shouldLoad",
                            offset: 0,
                          }, () => { this.props.handleCloseFlyout(); });
                        },
                        confirmButtonText: "Restore",
                        title: `Restore ${capitalizeFirstLetter(this.providerPlurality)}`,
                      }}
                      testId="network-providers-panel__restore-modal"
                      disabled={!this.state.selectedProviders.length}
                      classNames="p-0"
                    >
                      <QesIcon iconName={QesIcons.FontAwesome.User} isDecorative width="11" classNames="align-baseline" /> Restore
                    </ModalLink>
                  </TooltipWrapper>
                )}
            </RoleRestricted>
          )}

        {this.isZipProject && (
          <FlyoutLink
            content={{
              body: (
                <DisplayOptions
                  defaultOpen={true}
                  projectId={this.props.project.projectId}
                  // this will need to be updated when termination is added to zip projects
                  viewablePanels={[TableType.NetworkProviders]}
                  onChange={(isDirty: boolean) => { this.setState({ displayOptionsDirty: isDirty }) }}
                  relevantTableType={TableType.NetworkProviders}
                />
              ),
              title: "Display Options",
            }}
            modalContent={{
              body: "You will lose unsaved changes. Continue with closing the sidebar?",
              cancelButtonText: "Cancel",
              confirmButtonText: "Continue",
              title: "Warning",
            }}
            size="60"
            classNames={"qes-button-secondary text-nowrap p-0 ml-3"}
            warnOnClose={this.state.displayOptionsDirty}
            testId="network-providers-panel__viewable-fields-flyout-link"
          >
            Display Options
          </FlyoutLink>
        )}
      </>
    );
  };

  private getColumnConfig = () => {
    return buildAdequacyProvidersColumnConfig(
      this.props.viewableFieldsByTableType.get(this.state.tableType),
      this.state.tableType,
      this.props.project.showNppesDates && this.props.runConfiguration && this.props.runConfiguration.showNppesDates,
      this.props.project.showMipsScore && this.props.runConfiguration && this.props.runConfiguration.showMipsScore,
      this.props.project.showPciData && this.props.runConfiguration && this.props.runConfiguration.showPciData,
      this.props.project.showVaClaimsVolumeColumn,
      this.props.project.showTricareClaimsVolumeColumn,
      this.state.showAverageDistance,
      this.state.showAverageTime,
      false,
      false,
      false,
      this.props.project.showLocationConfidenceScores && this.props.runConfiguration && this.props.runConfiguration.showLocationConfidenceScores,
      this.props.project.showMotiveAppropriatenessScore && this.props.runConfiguration && this.props.runConfiguration?.showMotiveAppropriatenessScore,
      this.props.enabledFeatureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT),
      this.props.enabledFeatureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES) && this.props.runConfiguration?.showCareJourneyScores
    );
  }

  private getAliasString = () => {
    const hideAlias = this.props.adequacyResult?.aggregateSummary?.hideAlias ?? false;

    return !hideAlias && this.props.runConfiguration.networkProviderSetAlias
      ? ` (${this.props.runConfiguration.networkProviderSetAlias})`
      : "";
  };

  private getTitle = () => {
    const aliasString = this.getAliasString();
    const records = this.state.tableType === TableType.NetworkProviders
      ? this.state.sortedRecords
      : this.state.termedRecords;

    const shouldShowBatchCount = this.state.isOpen &&
      (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading");

    const batchCount = shouldShowBatchCount
      ? ` (${this.state.sortedRecords.length} of ${this.state.totalCount}${this.state.lastSearchValue ? `, filtered to "${this.state.lastSearchValue}"` : ""})`
      : "";

    if (this.props.nextClosestProviders) {
      return `Next Closest Network Providers${aliasString}${batchCount}`;
    }

    if (this.state.tableType === TableType.ProviderTerminations) {
      const terminationsCount = ` (${Math.min(this.state.offset, records.length)} of ${records.length}${this.state.lastSearchValue ? `, filtered to "${this.state.lastSearchValue}"` : ""})`;
      return `Network Servicing Provider Terminations${aliasString}${terminationsCount}`;
    }

    return `Network Servicing Providers${aliasString}${batchCount}`;
  };

  private terminationDisabledToolTip = (selectedProviders: ServicingProvider[]) => {

    if (this.props.user.roles.includes(Roles.TIN_VIEWER)) {
      // A mix of NPI-only and TIN-only is selected
      if (selectedProviders.some((provider) => !provider.npi && provider.tin) && selectedProviders.some((provider) => provider.npi && !provider.tin)) {
        return "All selected providers must have NPIs or all selected providers must have TINs.";
      }
    }

    return "";
  };
}

const WrappedNetworkProvidersPanel = withZipProjectContext(withFlyoutContext(withFeatureFlagContext(withUserContext(withViewableFields(withCountyProjectContext(NetworkProvidersPanel))))));

export { WrappedNetworkProvidersPanel as NetworkProvidersPanel };
